import { useState, useEffect } from 'react';
import {
  FundChartPeriod,
  UseFundSummaryResult,
} from '../../types/fundsummary.type';
import { errorMsg } from '../../constants/common.constant';
import useFundInfo from './useFundInfo';
import useFundQuote from './useFundQuote';
import useFundPerformance from './useFundPerformance';
import useFundLipperRating from './useFundLipperRating';
import useFundHoldings from './useFundHoldings';
import useFundChartData from './useFundChartData';

const useFundSummaryData = (
  ric: string = '',
  token: string = '',
  saleInCountryCode: string,
  period?: FundChartPeriod,
): UseFundSummaryResult => {
  const info = useFundInfo(ric, token, saleInCountryCode);
  const quote = useFundQuote(ric, token, saleInCountryCode);
  const performance = useFundPerformance(ric, token, saleInCountryCode);
  const lipperRating = useFundLipperRating(ric, token, saleInCountryCode);
  const holdings = useFundHoldings(ric, token, saleInCountryCode);
  const chart = useFundChartData(ric, token, period, saleInCountryCode);

  const [error, setError] = useState<string | null>(null);

  const isLoading =
    info.isLoading ||
    quote.isLoading ||
    performance.isLoading ||
    lipperRating.isLoading ||
    holdings.isLoading;

  const isChartLoading = chart.isLoading;

  const isSettling = isLoading || isChartLoading;

  useEffect(() => {
    if (isSettling) {
      setError(null);
      return;
    }
    const errorMessage = errorMsg.find(
      (obj) => obj.key === 'fundSummary',
    )?.label!;

    const failure =
      info.error ||
      (quote.error &&
        performance.error &&
        lipperRating.error &&
        holdings.error &&
        chart.error) ||
      (info.data?.fundDetailInfo && Object.keys(info.data?.fundDetailInfo)
        ? null
        : new Error(errorMessage));

    if (failure) {
      setError(errorMessage ?? null);
      return;
    }

    setError(null);
  }, [
    isSettling,
    info.data,
    info.error,
    quote.error,
    performance.data,
    performance.error,
    lipperRating.error,
    holdings.data,
    holdings.error,
    chart.error,
  ]);

  return {
    info: info.data,
    quote: quote.data,
    performance: performance.data,
    lipperRating: lipperRating.data,
    holdings: holdings.data,
    chart: chart.data,
    isLoading,
    isChartLoading,
    error,
  };
};

export default useFundSummaryData;
